import {
	createExternalComponentBridge,
	createPartytownAdapter,
	createRemoteReceiver,
	ExternalComponentBridge,
	getRemoteChannel,
	isFirstInsertMessage,
	RemotePortalCreator,
	RUI_ACTIONS,
} from '@wix/external-component-renderer'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { SiteFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import { name } from '../symbols'
import type { ExternalComponentSiteConfig, IConcreteExternalComponentLoader } from '../types'

let remotePortalCreatorPromise: Promise<RemotePortalCreator> | null = null

// This MUST be defined on global scope and not inside the feature.
// Partytown reads global scope functions in early stage, before the feature is loaded.
Object.defineProperty(globalThis, 'createRemotePortal', {
	value: async (...args: Parameters<RemotePortalCreator>) => {
		if (remotePortalCreatorPromise) {
			const remotePortalCreator = await remotePortalCreatorPromise
			return remotePortalCreator(...args)
		}
	},
})

const defineRemotePortalCreator = async (externalComponentBridge: ExternalComponentBridge) => {
	if (remotePortalCreatorPromise) {
		return
	}
	await window.externalsRegistry.react.loaded

	remotePortalCreatorPromise = import('@wix/external-component-renderer').then(({ getRemotePortalCreator }) => {
		return getRemotePortalCreator(externalComponentBridge)
	})
}

export const UntrustedExternalComponentLoader = withDependencies(
	[named(SiteFeatureConfigSymbol, name)],
	({ isRuiTrace }: ExternalComponentSiteConfig): IConcreteExternalComponentLoader => {
		let externalComponentBridge: ExternalComponentBridge | null = null

		return {
			type: 'untrusted',
			async loadComponent({ url, importKey = 'default', id, fullUrl, onError }) {
				if (!externalComponentBridge) {
					externalComponentBridge = createExternalComponentBridge({
						adapter: createPartytownAdapter(),
					})

					if (process.env.browser) {
						await defineRemotePortalCreator(externalComponentBridge)
					}
				}

				const receiver = createRemoteReceiver()
				const { channel, subscribe } = getRemoteChannel(receiver)
				const unsubscribeDidMount = subscribe((type, ...payload) => {
					if (isFirstInsertMessage(type, ...payload)) {
						unsubscribeDidMount()
					}
				})

				if (isRuiTrace) {
					subscribe((type, ...payload) => {
						console.log(`%crui-message (${id}):`, 'color: #f39c12;', RUI_ACTIONS[type], payload)
					})
				}

				const { render: remoteRender, unMount: remoteUnMount } = await externalComponentBridge!.loadComponent(
					{ componentId: id, moduleUrl: fullUrl, modulePath: url, importKey, onError },
					{ channel }
				)

				if (process.env.browser) {
					await window.externalsRegistry.react.loaded
				}

				// This import is done here by 2 reasons:
				// 1. To delay loading the component until we actually need it
				// 2. To make sure the component loads after TB loads React
				const { prepareExternalComponent, createController } = await import('@wix/external-component-renderer')
				const controller = createController({ allowHtml: true })

				return prepareExternalComponent({ receiver, controller, remoteRender, unMount: remoteUnMount })
			},
		}
	}
)
